/**
 * API client.
 *
 * `credentials: 'include'` on every call is what carries the vendor session
 * cookie. In production the portal and the API are the same origin so this is a
 * no-op; in local dev Vite proxies /api to :4000 and it matters.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code from the API, e.g. 'rate_limited_email'. */
  readonly code: string;
  readonly body: Record<string, unknown>;

  constructor(status: number, code: string, body: Record<string, unknown>) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Thrown when the request never reached the server — offline, DNS, dropped LTE. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'NetworkError';
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (rest.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...rest, headers, credentials: 'include' });
  } catch (err) {
    // A vendor at a gate loses signal mid-flow often enough that this needs to
    // be a distinct, recoverable state rather than a generic failure.
    throw new NetworkError(err);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      body = { raw: text };
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, String(body.error ?? `http_${res.status}`), body);
  }
  return body as T;
}

// ─────────────────────────────────────────────────────────── vendor surface

export interface SiteInfo {
  id: string;
  name: string;
  label: string | null;
  addressLine: string | null;
  timezone: string;
}

export interface OpenVisit {
  id: string;
  name: string;
  company: string;
  purpose: string;
  signedInAt: string;
  signedInAtLocal: string;
}

export interface MeResponse {
  email: string;
  site: SiteInfo;
  openVisit: OpenVisit | null;
  lastVisit: { name: string; company: string } | null;
}

export interface VisitPayload {
  id: string;
  name: string;
  company: string;
  purpose: string;
  signedInAtLocal: string;
  dateLine: string;
  site: SiteInfo;
}

export const vendorApi = {
  getSite: (siteId: string) =>
    request<{ site: SiteInfo }>(`/api/vendor/site/${encodeURIComponent(siteId)}`),

  requestCode: (siteId: string, email: string) =>
    request<{ status: 'sent' | 'session' }>('/api/vendor/code', {
      method: 'POST',
      body: JSON.stringify({ siteId, email }),
    }),

  verify: (siteId: string, email: string, code: string) =>
    request<{ status: 'verified'; expiresAt: string }>('/api/vendor/verify', {
      method: 'POST',
      body: JSON.stringify({ siteId, email, code }),
    }),

  me: () => request<MeResponse>('/api/vendor/me'),

  checkin: (input: { name: string; company: string; purposeKey: string; otherText?: string }) =>
    request<{ status: 'signed_in' | 'already_open'; visit: VisitPayload }>(
      '/api/vendor/checkin',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  checkout: () =>
    request<{
      status: 'signed_out';
      receiptSent: boolean;
      visit: {
        id: string;
        name: string;
        company: string;
        purpose: string;
        signedInAtLocal: string;
        signedOutAtLocal: string;
        dateLine: string;
        site: SiteInfo;
      };
    }>('/api/vendor/checkout', { method: 'POST' }),
};

// ──────────────────────────────────────────────────────────── staff surface

export interface AdminVisit {
  id: string;
  name: string;
  email: string;
  company: string;
  purpose: string;
  purposeKey: string;
  signedInAtLocal: string;
  signedOutAtLocal: string | null;
  closedReason: string | null;
  onSite: boolean;
}

export interface VisitsResponse {
  site: { id: string; name: string; label: string | null; timezone: string };
  date: string;
  visits: AdminVisit[];
  kpis: {
    onSite: number;
    visitsToday: number;
    /** Null when nothing has closed yet — render an em dash, never a 0. */
    avgVisitMinutes: number | null;
    companies: number;
  };
}

export interface AdminSite {
  id: string;
  name: string;
  label: string | null;
  city: string | null;
  state: string | null;
  timezone: string;
}

export const adminApi = {
  me: (token: string) =>
    request<{
      upn: string;
      name: string | null;
      permissions: string[];
      canViewVisits: boolean;
      canCloseVisits: boolean;
      canPrintPosters: boolean;
    }>('/api/admin/me', { token }),

  sites: (token: string) =>
    request<{ sites: AdminSite[]; posterBaseUrl: string | null }>('/api/admin/sites', { token }),

  visits: (token: string, siteId: string, date?: string) =>
    request<VisitsResponse>(
      `/api/admin/visits?siteId=${encodeURIComponent(siteId)}${date ? `&date=${date}` : ''}`,
      { token },
    ),

  closeVisit: (token: string, id: string) =>
    request<{ visit: AdminVisit }>(`/api/admin/visits/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      token,
    }),

  csvUrl: (siteId: string, date: string) =>
    `/api/admin/visits.csv?siteId=${encodeURIComponent(siteId)}&date=${encodeURIComponent(date)}`,
};
